'use client'
import React, { useEffect, useRef, useState, useImperativeHandle, ReactNode } from 'react'

import { ChangeHandler } from 'react-hook-form'

export interface FormCheckboxProps extends Omit<React.HTMLAttributes<HTMLInputElement>, 'onChange'> {
    label?: string | ReactNode
    id?: string
    form?: string
    name?: string
    value?: string | number
    checked?: boolean
    disabled?: boolean
    readOnly?: boolean
    indeterminate?: boolean
    onChange?: ChangeHandler | ((value: unknown, checked: boolean) => void)
    /** Adds a box around the checkbox */
    boxed?: boolean
}

export const FormCheckbox = React.forwardRef<HTMLInputElement, FormCheckboxProps>(
    (
        { label, id, form, name, value, checked, disabled, readOnly, indeterminate = false, onChange, boxed, ...rest },
        ref,
    ) => {
        const [checkedValue, setCheckedValue] = useState(checked)
        const checkboxRef = useRef<HTMLInputElement>(null)
        useImperativeHandle(ref, () => checkboxRef.current!, [ref])

        const onChangeHandler = () => {
            if (!readOnly && !disabled) {
                setCheckedValue(!checkedValue)
            }
        }

        useEffect(() => {
            if (checkboxRef?.current) {
                checkboxRef.current.indeterminate = !!indeterminate
            }
        }, [indeterminate])

        const labelClasses = `
            ${boxed ? 'checkbox--boxed' : ''}
            ${disabled ? 'checkbox--disabled' : ''}
            ${readOnly ? 'checkbox--readonly' : ''}
        `

        return (
            <label className={`checkbox ${labelClasses}`}>
                <input
                    {...rest}
                    ref={checkboxRef}
                    type="checkbox"
                    id={id}
                    name={name}
                    value={value}
                    form={form}
                    disabled={disabled}
                    className="checkbox__input"
                    onChange={(e) => {
                        onChange && onChange(e, !checkedValue)
                        onChangeHandler()
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Space') {
                            onChange && onChange(e, !checkedValue)
                            onChangeHandler()
                        }
                    }}
                    checked={checkedValue}
                    data-cy="input-checkbox"
                />
                {label && <span className="checkbox__label">{label}</span>}
            </label>
        )
    },
)
